import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from '../auth';
import { LoadingPane, PageHeader } from '../components/page-elements';

const isSelectableRailcarStatus = (status) => {
  const normalized = String(status || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return normalized === 'available' || normalized === 'onspot';
};

const CreateBOL = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { order } = location.state || {};
  const user = useMemo(() => getStoredUser(), []);

  const [orderDetails, setOrderDetails] = useState(order || null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [formData, setFormData] = useState({
    bolDate: new Date(),
    railcarID: order?.railcarID || '',
    groundInventoryLot: order?.groundInventoryLot?._id || order?.groundInventoryLot || '',
    secondaryGroundInventoryLot: order?.secondaryGroundInventoryLot?._id || order?.secondaryGroundInventoryLot || '',
    truckID: '',
    trailerID: '',
    tareWeight: '',
    comments: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableRailcars, setAvailableRailcars] = useState([]);
  const [availableRailcarsLoading, setAvailableRailcarsLoading] = useState(false);
  const [availableRailcarsError, setAvailableRailcarsError] = useState('');
  const [groundInventoryLots, setGroundInventoryLots] = useState([]);
  const [groundInventoryLoading, setGroundInventoryLoading] = useState(false);

  useEffect(() => {
    if (!order?._id) return;
    setOrderLoading(true);

    fetch(`${API_BASE_URL}/api/orders/${order._id}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setOrderDetails(data);
        setFormData((prev) => ({
          ...prev,
          railcarID: data.railcarID || prev.railcarID || '',
          groundInventoryLot: data.groundInventoryLot?._id || data.groundInventoryLot || prev.groundInventoryLot || '',
          secondaryGroundInventoryLot:
            data.secondaryGroundInventoryLot?._id || data.secondaryGroundInventoryLot || prev.secondaryGroundInventoryLot || '',
        }));
      })
      .catch(() => setOrderDetails(order))
      .finally(() => setOrderLoading(false));
  }, [order]);

  useEffect(() => {
    const inventorySource = orderDetails?.inventorySource === 'ground' ? 'ground' : 'railcar';
    const customerId = orderDetails?.customerName?._id || orderDetails?.customerName;

    if (!customerId || inventorySource !== 'railcar') {
      setAvailableRailcars([]);
      return;
    }

    setAvailableRailcarsLoading(true);
    setAvailableRailcarsError('');
    fetch(`${API_BASE_URL}/api/railcars?customerId=${customerId}`, {
      headers: getAuthHeaders(),
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => []);
        if (!res.ok) {
          throw new Error(payload?.message || 'Failed to load available railcars');
        }
        return Array.isArray(payload) ? payload : [];
      })
      .then((rows) => setAvailableRailcars(rows.filter((row) => isSelectableRailcarStatus(row?.currentStatus))))
      .catch((err) => {
        setAvailableRailcars([]);
        setAvailableRailcarsError(err.message || 'Failed to load available railcars');
      })
      .finally(() => setAvailableRailcarsLoading(false));
  }, [orderDetails]);

  useEffect(() => {
    const inventorySource = orderDetails?.inventorySource === 'ground' ? 'ground' : 'railcar';
    const customerId = orderDetails?.customerName?._id || orderDetails?.customerName;
    const materialId = orderDetails?.materialName?._id || orderDetails?.materialName;
    if (!customerId || !materialId || inventorySource !== 'ground') {
      setGroundInventoryLots([]);
      return;
    }

    setGroundInventoryLoading(true);
    const params = new URLSearchParams({
      customerId: String(customerId),
      materialId: String(materialId),
      status: 'available',
    });
    fetch(`${API_BASE_URL}/api/ground-inventory?${params.toString()}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((payload) => {
        const lots = Array.isArray(payload?.lots) ? payload.lots : [];
        setGroundInventoryLots(lots.filter((lot) => Number(lot.remainingWeight || 0) > 0));
        setFormData((prev) => (prev.groundInventoryLot || !lots[0]?._id ? prev : { ...prev, groundInventoryLot: lots[0]._id }));
      })
      .catch(() => setGroundInventoryLots([]))
      .finally(() => setGroundInventoryLoading(false));
  }, [orderDetails]);

  if (!(user?.role === 'internal' || user?.role === 'admin')) {
    return <Typography>Only internal users can create BOL drafts.</Typography>;
  }

  if (!orderDetails) {
    return (
      <Box p={2}>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Select an order first, then create a BOL draft from the Orders screen.
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/orders')}>
          Go to Orders
        </Button>
      </Box>
    );
  }

  if (orderLoading) return <LoadingPane label="Loading order details..." />;

  const preferredRailcar = String(orderDetails.railcarID || '').trim();
  const inventorySource = orderDetails.inventorySource === 'ground' ? 'ground' : 'railcar';
  const primaryGroundLotId = orderDetails.groundInventoryLot?._id || orderDetails.groundInventoryLot || '';
  const primaryGroundLotRailcarId = orderDetails.groundInventoryLot?.sourceRailcarID
    || groundInventoryLots.find((lot) => String(lot?._id || '') === String(primaryGroundLotId || ''))?.sourceRailcarID
    || '';
  const dropdownRows = [...availableRailcars];
  const hasPreferredInDropdown = preferredRailcar
    && dropdownRows.some((row) => String(row.railcarID || '').trim() === preferredRailcar);

  if (preferredRailcar && !hasPreferredInDropdown) {
    dropdownRows.unshift({
      _id: '__preferred__',
      railcarID: preferredRailcar,
      station: '',
      track: '',
      currentStatus: 'Preferred',
    });
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, bolDate: date }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.bolDate) {
      setError('BOL date is required.');
      setLoading(false);
      return;
    }

    if (inventorySource === 'railcar' && !String(formData.railcarID || '').trim()) {
      setError('Railcar ID is required.');
      setLoading(false);
      return;
    }
    if (inventorySource === 'ground' && !String(formData.groundInventoryLot || '').trim()) {
      setError('Ground inventory lot is required.');
      setLoading(false);
      return;
    }
    if (
      inventorySource === 'ground'
      && Boolean(orderDetails.splitLoad)
      && !String(formData.secondaryGroundInventoryLot || '').trim()
    ) {
      setError('Secondary ground inventory lot is required for split ground loads.');
      setLoading(false);
      return;
    }

    if (!String(formData.truckID || '').trim()) {
      setError('Truck ID is required.');
      setLoading(false);
      return;
    }

    if (!String(formData.trailerID || '').trim()) {
      setError('Trailer ID is required.');
      setLoading(false);
      return;
    }

    if (formData.tareWeight !== '' && Number(formData.tareWeight) < 0) {
      setError('Truck tare weight cannot be negative.');
      setLoading(false);
      return;
    }

    const bolData = {
      orderNumber: orderDetails._id,
      bolDate: formData.bolDate.toISOString(),
      customerName: orderDetails.customerName?._id || orderDetails.customerName,
      shipperName: orderDetails.shipperName?._id || orderDetails.shipperName,
      projectName: orderDetails.projectName?._id || orderDetails.projectName,
      materialName: orderDetails.materialName?._id || orderDetails.materialName,
      inventorySource,
      groundInventoryLot: inventorySource === 'ground' ? String(formData.groundInventoryLot || '').trim() : null,
      secondaryGroundInventoryLot:
        inventorySource === 'ground' && Boolean(orderDetails.splitLoad)
          ? String(formData.secondaryGroundInventoryLot || '').trim()
          : null,
      railcarID: inventorySource === 'ground' ? '' : String(formData.railcarID || '').trim(),
      truckID: String(formData.truckID || '').trim(),
      trailerID: String(formData.trailerID || '').trim(),
      tareWeight: formData.tareWeight === '' ? null : Number(formData.tareWeight),
      comments: formData.comments,
      splitLoad: Boolean(orderDetails.splitLoad),
      secondaryRailcarID: inventorySource === 'ground' ? '' : orderDetails.splitLoad ? String(orderDetails.secondaryRailcarID || '').trim() : '',
      status: 'Draft',
      createdBy: user._id,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/bols`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(bolData),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to create BOL draft');
      }

      setSuccess('BOL draft created successfully. Internal users can now complete it with weights.');
      setFormData({
        bolDate: new Date(),
        railcarID: '',
        groundInventoryLot: '',
        secondaryGroundInventoryLot: '',
        truckID: '',
        trailerID: '',
        tareWeight: '',
        comments: '',
      });
      setTimeout(() => navigate('/bols'), 700);
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2}>
        <PageHeader
          title="Create BOL Draft"
          subtitle="Start the internal check-in by capturing truck and railcar details before completion."
          actions={[
            <Button key="back" variant="outlined" onClick={() => navigate('/orders')}>
              Back to Orders
            </Button>,
          ]}
        />

        {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert> : null}
        {inventorySource === 'railcar' && availableRailcarsError ? <Alert severity="warning" sx={{ mb: 1.5 }}>{availableRailcarsError}</Alert> : null}
        {inventorySource === 'railcar' && !availableRailcarsLoading && dropdownRows.length === 0 ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            No available or on-spot railcars found for this customer. Set a preferred railcar on the order or update railcar availability.
          </Alert>
        ) : null}
        {inventorySource === 'ground' && !groundInventoryLoading && groundInventoryLots.length === 0 ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            No available ground inventory lots found for this customer/material.
          </Alert>
        ) : null}

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={5} lg={4}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>
                  Order Information
                </Typography>
                <Stack spacing={0.65}>
                  <Typography variant="body2"><strong>Order Number:</strong> {orderDetails.orderNumber}</Typography>
                  <Typography variant="body2"><strong>Customer:</strong> {orderDetails.customerName?.customerName || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Receiver:</strong> {orderDetails.receiverName?.receiverName || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Project:</strong> {orderDetails.projectName?.projectName || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Material:</strong> {orderDetails.materialName?.materialName || 'N/A'}</Typography>
                </Stack>
                <Divider sx={{ my: 1.2 }} />
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={`Status ${orderDetails.orderStatus || 'Draft'}`} color="info" variant="outlined" />
                  <Chip size="small" label={`Source ${inventorySource === 'ground' ? 'Ground' : 'Railcar'}`} color={inventorySource === 'ground' ? 'success' : 'default'} />
                  {inventorySource === 'ground' ? (
                    <Chip size="small" label={`Primary ${primaryGroundLotRailcarId || 'Manual Lot'}`} />
                  ) : (
                    <Chip size="small" label={`Preferred Railcar ${orderDetails.railcarID || 'Site Assigned'}`} />
                  )}
                  {orderDetails.splitLoad ? (
                    <Chip
                      size="small"
                      label={
                        inventorySource === 'ground'
                          ? `Secondary Lot ${orderDetails.secondaryGroundInventoryLot?._id || orderDetails.secondaryGroundInventoryLot || 'N/A'}`
                          : `Secondary ${orderDetails.secondaryRailcarID || 'Site Assigned'}`
                      }
                      color="warning"
                      variant="outlined"
                    />
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7} lg={8}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Draft Entry
                </Typography>
                <Grid container spacing={1.2}>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label="BOL Date"
                      value={formData.bolDate}
                      onChange={handleDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                    />
                  </Grid>
                  {inventorySource === 'ground' ? (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Ground Inventory Lot"
                        name="groundInventoryLot"
                        select
                        value={formData.groundInventoryLot || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                        helperText={groundInventoryLoading ? 'Loading ground lots...' : 'Select lot for this load'}
                      >
                        {groundInventoryLots.map((lot) => (
                          <MenuItem key={lot._id} value={lot._id}>
                            {(lot.sourceRailcarID || 'Manual Lot')} - {Number(lot.remainingWeight || 0).toLocaleString()} lbs{lot.notes ? ` | Note: ${String(lot.notes)}` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Railcar ID"
                        name="railcarID"
                        select
                        value={formData.railcarID}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                        helperText={
                          availableRailcarsLoading
                            ? 'Loading available/on-spot customer railcars...'
                            : (preferredRailcar
                              ? 'Preferred railcar from order is pre-selected when available.'
                              : 'Order is site-assigned. Select from available or on-spot customer railcars.')
                        }
                      >
                        <MenuItem value="" disabled>
                          {availableRailcarsLoading ? 'Loading railcars...' : 'Select railcar'}
                        </MenuItem>
                        {dropdownRows.map((railcar) => {
                          const railcarId = String(railcar.railcarID || '').trim();
                          const secondary = [railcar.station, railcar.track].filter(Boolean).join(' / ');
                          const status = railcar.currentStatus ? ` (${railcar.currentStatus})` : '';
                          return (
                            <MenuItem key={railcar._id || railcarId} value={railcarId}>
                              {railcarId}{secondary ? ` - ${secondary}` : ''}{status}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Truck ID"
                      name="truckID"
                      value={formData.truckID}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Trailer ID"
                      name="trailerID"
                      value={formData.trailerID}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Truck Tare Weight (Check-in)"
                      name="tareWeight"
                      value={formData.tareWeight}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Comments"
                      name="comments"
                      value={formData.comments}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Draft'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default CreateBOL;
